import { Actor, HttpAgent, type Identity } from '@icp-sdk/core/agent';
import { Principal } from '@icp-sdk/core/principal';
import { idlFactory, type _SERVICE as TraderService, type Trader, type Trade, type Snapshot, type Mode, type NotificationPage } from '../bindings/traders/declarations/traders.did';
import { factory, HOST, type BaseToken } from './api';
import { amount } from './format';
import type { DisplayTrade } from '../components/Trades';

export interface TraderSummary {
  owner: string; name?: string; realizedPnlUsd: number | null; realizedCostUsd: number | null; roi: number | null;
  matchedSells: number; wins: number; losses: number; trades: number; volumeUsd: number | null; lastTradeAt: bigint;
  unmatchedSells: number; unpricedTrades: number;
}
export interface TraderBoard {
  rows: TraderSummary[]; indexedTrades: number; complete: boolean; oldestAt: bigint | null; latestAt: bigint | null;
  updatedAt: bigint; error?: string; mode: 'native' | 'local';
}
export interface TraderDetail extends Omit<TraderBoard, 'rows'> { summary: TraderSummary | null; trades: DisplayTrade[]; totalTrades: number; nextBefore: bigint | null }
export interface TraderNotice { id: bigint; trader: string; market: string; symbol: string; buy: boolean; at: bigint; tokens: bigint; dollars: number | null; read: boolean }
type RawNotification = NotificationPage['items'][number];
let publicActor: Promise<ReturnType<typeof Actor.createActor<TraderService>>> | undefined;
export async function traderService(identity?: Identity) {
  const create = async () => {
    const id = import.meta.env.VITE_TRADERS_CANISTER_ID;
    if (!id) throw Error('Deploy the traders canister to enable trader rankings and alerts.');
    const local = import.meta.env.VITE_DFX_NETWORK === 'local';
    const agent = await HttpAgent.create({ host: local ? import.meta.env.VITE_DFX_HOST : HOST, identity, fetch: (input, init) => fetch(input, { ...init, signal: init?.signal ? AbortSignal.any([init.signal, AbortSignal.timeout(20_000)]) : AbortSignal.timeout(20_000) }) });
    if (local) await agent.fetchRootKey();
    return Actor.createActor<TraderService>(idlFactory, { agent, canisterId: id });
  };
  if (identity) return create();
  return publicActor ??= create().catch(error => { publicActor = undefined; throw error; });
}
export function traderResult<T>(result: { ok: T } | { err: string }): T { if ('err' in result) throw Error(result.err); return result.ok; }
const finite = (value: number) => Number.isFinite(value) ? value : null;
const names = new Map<string, { value: string; until: number }>();
async function displayNames(owners: Principal[]) {
  const missing = owners.filter(owner => (names.get(owner.toText())?.until ?? 0) <= Date.now());
  if (!missing.length) return;
  try {
    const source = await factory();
    for (let offset = 0; offset < missing.length; offset += 100) {
      const batch = missing.slice(offset, offset + 100);
      const rows = await source.names(batch);
      batch.forEach(owner => names.set(owner.toText(), { value: '', until: Date.now() + 300_000 }));
      rows.forEach(([owner, value]) => names.set(owner.toText(), { value, until: Date.now() + 300_000 }));
    }
  } catch { /* Names are optional; principals remain the source of identity. */ }
}
function summary(row: Trader): TraderSummary {
  return { owner: row.who.toText(), name: names.get(row.who.toText())?.value || undefined, realizedPnlUsd: row.matched_sells ? finite(row.realized_pnl_usd) : null,
    realizedCostUsd: row.matched_sells ? finite(row.matched_cost_usd) : null, roi: row.matched_cost_usd > 0 ? finite(row.realized_pnl_usd / row.matched_cost_usd * 100) : null,
    matchedSells: Number(row.matched_sells), wins: Number(row.winning_sells), losses: Number(row.losing_sells), trades: Number(row.trades),
    volumeUsd: row.unpriced_trades ? null : finite(row.buy_volume_usd + row.sell_volume_usd), lastTradeAt: row.last_trade_at, unmatchedSells: Number(row.unmatched_sells), unpricedTrades: Number(row.unpriced_trades) };
}
function coverage(snapshot: Snapshot | undefined, mode: Mode, indexed?: bigint, error?: string): Omit<TraderBoard, 'rows'> {
  return { indexedTrades: Number(snapshot?.trade_count ?? indexed ?? 0n), complete: Boolean(snapshot), oldestAt: snapshot?.first_trade_at[0] ?? null, latestAt: snapshot?.last_trade_at[0] ?? null, updatedAt: snapshot?.updated_at ?? 0n, mode: 'native' in mode ? 'native' : 'local', error };
}
export async function loadTraderBoard(): Promise<TraderBoard> {
  const service = await traderService();
  const first = await service.leaderboard({ offset: 0n, limit: 100n, expected_version: [] });
  const rows = [...first.items]; let next = first.next_offset[0];
  while (next !== undefined) {
    if (rows.length > 20_000) throw Error('The trader leaderboard exceeds this display’s capacity.');
    const page = await service.leaderboard({ offset: next, limit: 100n, expected_version: first.snapshot[0] ? [first.snapshot[0].version] : [] });
    if (!page.page_valid || !page.items.length) throw Error('Trader rankings changed while loading. Refresh to read a consistent snapshot.');
    rows.push(...page.items); next = page.next_offset[0];
  }
  await displayNames(rows.map(row => row.who));
  return { rows: rows.map(summary), ...coverage(first.snapshot[0], first.mode, first.indexed, first.error[0]) };
}
let bases: { until: number; promise: Promise<BaseToken[]> } | undefined;
function baseMetadata() {
  if (bases && bases.until > Date.now()) return bases.promise;
  const promise = factory().then(service => service.config()).then(config => config.bases.map(item => item.token)).catch(() => { bases = undefined; return []; });
  bases = { until: Date.now() + 300_000, promise }; return promise;
}
function displayTrade(trade: Trade, tokens: BaseToken[]): DisplayTrade {
  const dollars = trade.usd[0] !== undefined && trade.usd[0] > 0 && trade.base > 0n && trade.tokens > 0n ? finite(trade.usd[0]) : null;
  const base = tokens.find(token => token.ledger.toText() === trade.base_ledger[0]?.toText());
  return { id: `social:${trade.id}`, at: trade.at, who: trade.who.toText(), buy: trade.buy, usd: dollars, price: dollars === null || trade.tokens === 0n ? null : dollars / (Number(trade.tokens) / 1e8), tokens: trade.tokens, base: base ? amount(trade.base, base.decimals, 6) : '—', baseSymbol: base?.symbol ?? '', market: trade.market.toText(), symbol: trade.symbol };
}
export async function loadTraderDetail(owner: string, before?: bigint): Promise<TraderDetail> {
  const service = await traderService(), who = Principal.fromText(owner);
  const [page, config, tokens] = await Promise.all([service.trader_trades(who, before === undefined ? [] : [before], 100n), service.index_config(), baseMetadata()]);
  await displayNames([who]);
  const row = page.summary[0];
  return { summary: row ? summary(row) : null, trades: page.items.map(trade => displayTrade(trade, tokens)), totalTrades: Number(row?.trades ?? 0n), nextBefore: page.next_before[0] ?? null, ...coverage(page.snapshot[0], config.mode, config.indexed, config.error[0]) };
}
export function traderNotice(raw: RawNotification): TraderNotice {
  return { id: raw.id, trader: raw.trade.who.toText(), market: raw.trade.market.toText(), symbol: raw.trade.symbol, buy: raw.trade.buy, at: raw.trade.at, tokens: raw.trade.tokens, dollars: raw.trade.usd[0] === undefined ? null : finite(raw.trade.usd[0]), read: raw.read };
}
